import React from "react";
import {
  Box,
  Button,
  Divider,
  FormControlLabel,
  IconButton,
  Switch,
  TextField,
  Typography,
  Paper,
  Grid,
} from "@mui/material";
import { useFormik } from "formik";
import CancelIcon from "@mui/icons-material/Cancel";
import * as Yup from "yup";
import axiosInstance from "../../services/axiosInstance";
import { SA_CREATE_PERMISSION } from "../../shared/constants/apiEndpoints";
import { toast } from "react-toastify";

interface CreatePermissionDrawerProps {
  onCloseDrawer: () => void;
  onSuccess?: () => void;
  initialParentId?: string;
}

const validationSchema = Yup.object().shape({
  permission_name: Yup.string().trim().required("Permission Name is required"),
  permission_code: Yup.string().trim().required("Permission Code is required"),
  parent_id: Yup.string().nullable(),
});

const DEFAULT_ROLES = [
  { key: "Admin", label: "Admin", defaultChecked: true, disabled: true },
  { key: "Regulator", label: "Regulator", defaultChecked: true, disabled: false },
  { key: "Read & Run", label: "Read & Run", defaultChecked: false, disabled: false },
  { key: "Read-Only", label: "Read-Only", defaultChecked: false, disabled: true },
  { key: "Individual User", label: "Individual User", defaultChecked: false, disabled: false },
  { key: "ApexaRole", label: "ApexaRole", defaultChecked: false, disabled: false },
];

const CreatePermissionDrawer: React.FC<CreatePermissionDrawerProps> = ({
  onCloseDrawer,
  onSuccess,
  initialParentId,
}) => {
  const formik = useFormik({
    initialValues: {
      permission_name: "",
      permission_code: "",
      parent_id: initialParentId || "",
      role_defaults: {
        Admin: true,
        "Read & Run": false,
        "Read-Only": false,
        Regulator: true,
        "Individual User": false,
        ApexaRole: false,
      },
    },
    enableReinitialize: true,
    validationSchema,
    onSubmit: async (values, { setSubmitting }) => {
      const payload = {
        permission_name: values.permission_name.trim(),
        permission_code: values.permission_code.trim(),
        parent_id: values.parent_id?.trim() ? values.parent_id.trim() : null,
        role_defaults: values.role_defaults,
      };

      try {
        const response = await axiosInstance.post(SA_CREATE_PERMISSION, payload);
        toast.success(response?.data?.message || "Permission created successfully");
        if (onSuccess) {
          onSuccess();
        }
        onCloseDrawer();
      } catch (error: any) {
        console.error("Error creating permission:", error);
        toast.error(
          error?.response?.data?.message ||
            error?.message ||
            "Failed to create permission"
        );
      } finally {
        setSubmitting(false);
      }
    },
  });

  const handleRoleToggle = (roleKey: string) => (event: React.ChangeEvent<HTMLInputElement>) => {
    formik.setFieldValue(`role_defaults.${roleKey}`, event.target.checked);
  };

  return (
    <Box sx={{ width: "100%", height: "100%", display: "flex", flexDirection: "column" }}>
      {/* Drawer Header */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          px: 3,
          py: 2,
        }}
      >
        <Typography variant="h6" sx={{ fontSize: "18px", fontWeight: "bold" }}>
          Create Permission
        </Typography>
        <IconButton onClick={onCloseDrawer} size="small">
          <CancelIcon sx={{ color: "#A6A5B6" }} />
        </IconButton>
      </Box>
      <Divider />

      {/* Drawer Form Body */}
      <Box component="form" onSubmit={formik.handleSubmit} sx={{ p: 3, flexGrow: 1, overflowY: "auto" }}>
        <Grid container spacing={2.5}>
          <Grid item xs={12}>
            <TextField
              id="permission_name"
              name="permission_name"
              label="Permission Name *"
              variant="outlined"
              fullWidth
              size="small"
              value={formik.values.permission_name}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.touched.permission_name && Boolean(formik.errors.permission_name)}
              helperText={formik.touched.permission_name && formik.errors.permission_name}
              InputLabelProps={{ sx: { fontWeight: 600 } }}
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              id="permission_code"
              name="permission_code"
              label="Permission Code *"
              variant="outlined"
              fullWidth
              size="small"
              value={formik.values.permission_code}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.touched.permission_code && Boolean(formik.errors.permission_code)}
              helperText={formik.touched.permission_code && formik.errors.permission_code}
              InputLabelProps={{ sx: { fontWeight: 600 } }}
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              id="parent_id"
              name="parent_id"
              label="Parent ID (Optional)"
              variant="outlined"
              fullWidth
              size="small"
              value={formik.values.parent_id}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              helperText="Leave empty for top-level permission"
              InputLabelProps={{ sx: { fontWeight: 600 } }}
            />
          </Grid>

          <Grid item xs={12}>
            <Typography variant="subtitle2" sx={{ fontWeight: "bold", mb: 1, color: "text.primary" }}>
              Role Defaults
            </Typography>
            <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, bgcolor: "background.paper" }}>
              <Grid container spacing={1.5}>
                {DEFAULT_ROLES.map((role) => {
                  const isChecked = (formik.values.role_defaults as any)[role.key] ?? false;
                  return (
                    <Grid item xs={12} sm={6} key={role.key}>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={isChecked}
                            onChange={handleRoleToggle(role.key)}
                            disabled={role.disabled}
                            color="primary"
                            size="small"
                          />
                        }
                        label={
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {role.label}
                          </Typography>
                        }
                      />
                    </Grid>
                  );
                })}
              </Grid>
            </Paper>
          </Grid>
        </Grid>

        {/* Action Buttons */}
        <Box sx={{ mt: 4, display: "flex", justifyContent: "flex-end", gap: 2 }}>
          <Button variant="outlined" color="inherit" onClick={onCloseDrawer} size="medium">
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            color="primary"
            size="medium"
            disabled={!(formik.isValid && formik.dirty) || formik.isSubmitting}
            sx={{ minWidth: 120 }}
          >
            {formik.isSubmitting ? "Creating..." : "Create Permission"}
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

export default CreatePermissionDrawer;
